import { useState, useEffect, useRef } from"react";
import { useOpenFromQuery } from "@/hooks/useOpenFromQuery";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Plus, DollarSign, CheckCircle, CalendarPlus, ChevronDown, ChevronRight, Building2, X, Upload, FileText, Image, Bell } from"lucide-react";
import { toast } from"sonner";

const ReservationsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [reservations, setReservations] = useState([]);
 const [units, setUnits] = useState([]);
 const [projects, setProjects] = useState([]);
 const [buildings, setBuildings] = useState([]);
 const [allUnits, setAllUnits] = useState([]);
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [detailOpen, setDetailOpen] = useState(false);
 const [confirmSaleOpen, setConfirmSaleOpen] = useState(false);
 const [selectedReservation, setSelectedReservation] = useState(null);
 useOpenFromQuery("reservationId", reservations, (r) => { setSelectedReservation(r); setDetailOpen(true); });
 const [expandedProjects, setExpandedProjects] = useState({});
 const [selectedProjectId, setSelectedProjectId] = useState("");
 const [formData, setFormData] = useState({
 unit_id:"",
 buyer_name:"",
 buyer_phone:"",
 buyer_email:"",
 contract_value:"",
 deposit_amount:"2000",
 deposit_paid: false,
 payment_plan: []
 });
 const [contractData, setContractData] = useState({
 apartment_price:"",
 payment_method:"cash",
 down_payment:"",
 down_payment_date: new Date().toISOString().split('T')[0],
 payment_schedule_type:"lump_sum", //"lump_sum"or"recurring"
 // Lump sum milestone payments
 milestone_payments: [
 { description:"", amount:"", due_date:""}
 ],
 // Recurring payments
 recurring_amount:"",
 recurring_frequency:"monthly", //"monthly"or"yearly"
 recurring_start_date:"",
 recurring_duration:"", // number of payments
 contract_document_url:"",
 contract_file: null,
 contract_file_name:"",
 notes:""
 });
 const [uploadingContract, setUploadingContract] = useState(false);
 const contractFileInputRef = useRef(null);

 // Helper functions for milestone payments
 const addMilestonePayment = () => {
 setContractData({
 ...contractData,
 milestone_payments: [
 ...contractData.milestone_payments,
 { description:"", amount:"", due_date:""}
 ]
 });
 };

 const removeMilestonePayment = (index) => {
 const updated = contractData.milestone_payments.filter((_, i) => i !== index);
 setContractData({ ...contractData, milestone_payments: updated });
 };

 const updateMilestonePayment = (index, field, value) => {
 const updated = [...contractData.milestone_payments];
 updated[index] = { ...updated[index], [field]: value };
 setContractData({ ...contractData, milestone_payments: updated });
 };

 // Calculate remaining balance
 const calculateRemainingBalance = () => {
 const price = parseFloat(contractData.apartment_price) || 0;
 const downPayment = parseFloat(contractData.down_payment) || 0;
 
 if (contractData.payment_schedule_type ==="lump_sum") {
 const milestoneTotal = contractData.milestone_payments.reduce(
 (sum, m) => sum + (parseFloat(m.amount) || 0), 0
 );
 return price - downPayment - milestoneTotal;
 } else {
 const recurringAmount = parseFloat(contractData.recurring_amount) || 0;
 const duration = parseInt(contractData.recurring_duration) || 0;
 const recurringTotal = recurringAmount * duration;
 return price - downPayment - recurringTotal;
 }
 };

 useEffect(() => { loadData(); }, []);

 const loadData = async () => {
 try {
 const [reservationsRes, unitsRes, projectsRes, buildingsRes, allUnitsRes] = await Promise.all([
 axios.get("/reservations"),
 axios.get("/units?status=available"),
 axios.get("/projects"),
 axios.get("/buildings"),
 axios.get("/units")
 ]);
 setReservations(reservationsRes.data || []);
 setUnits(unitsRes.data || []);
 setProjects(projectsRes.data || []);
 setBuildings(buildingsRes.data || []);
 setAllUnits(allUnitsRes.data || []);
 
 // Initialize all projects as collapsed by default
 const expanded = {};
 projectsRes.data.forEach(project => {
 expanded[project.id] = false;
 });
 setExpandedProjects(expanded);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const toggleProject = (projectId) => {
 setExpandedProjects(prev => ({
 ...prev,
 [projectId]: !prev[projectId]
 }));
 };

 // Group reservations by project
 const getReservationsGroupedByProject = () => {
 const grouped = {};
 
 projects.forEach(project => {
 grouped[project.id] = {
 project: project,
 reservations: []
 };
 });

 reservations.forEach(reservation => {
 const unit = allUnits.find(u => u.id === reservation.unit_id);
 if (unit) {
 // Get the building for this unit
 const building = buildings.find(b => b.id === unit.building_id);
 if (building && grouped[building.project_id]) {
 grouped[building.project_id].reservations.push({
 ...reservation,
 unit_code: unit.code
 });
 }
 }
 });

 return grouped;
 };

 // Get available units filtered by selected project
 const getAvailableUnitsForProject = () => {
 if (!selectedProjectId) {
 return [];
 }
 
 // Get buildings for the selected project
 const projectBuildings = buildings.filter(b => b.project_id === selectedProjectId);
 const buildingIds = projectBuildings.map(b => b.id);
 
 // Filter available units that belong to these buildings
 return units.filter(u => buildingIds.includes(u.building_id));
 };

 const openConfirmSaleForm = () => {
 // Pre-fill with reservation data
 const today = new Date().toISOString().split('T')[0];
 setContractData({
 apartment_price: selectedReservation?.contract_value ||"",
 payment_method:"cash",
 down_payment: selectedReservation?.deposit_amount ||"",
 down_payment_date: today,
 payment_schedule_type:"lump_sum",
 milestone_payments: [{ description:"", amount:"", due_date:""}],
 recurring_amount:"",
 recurring_frequency:"monthly",
 recurring_start_date:"",
 recurring_duration:"",
 contract_document_url:"",
 contract_file: null,
 contract_file_name:"",
 notes:""
 });
 setDetailOpen(false);
 setConfirmSaleOpen(true);
 };

 // Handle contract file selection
 const handleContractFileSelect = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 // Validate file type
 const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
 if (!allowedTypes.includes(file.type)) {
 toast.error(t("validation.required"));
 return;
 }

 // Validate file size (max 25MB)
 if (file.size > 25 * 1024 * 1024) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 setUploadingContract(true);
 const formData = new FormData();
 formData.append('file', file);

 const response = await axios.post('/upload/contract', formData, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });

 setContractData({
 ...contractData,
 contract_document_url: response.data.url,
 contract_file_name: file.name
 });
 toast.success(t("messages.saveSuccess"));
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to upload contract document");
 } finally {
 setUploadingContract(false);
 }
 };

 const handleConfirmSale = async (e) => {
 e.preventDefault();
 
 // Validate contract document is uploaded
 if (!contractData.contract_document_url || contractData.contract_document_url.trim() ==="") {
 toast.error(t("validation.required"));
 return;
 }

 // Validate recurring payments fields if that option is selected
 if (contractData.payment_schedule_type ==="recurring") {
 if (!contractData.recurring_start_date) {
 toast.error(t("validation.required"));
 return;
 }
 if (!contractData.recurring_amount || parseFloat(contractData.recurring_amount) <= 0) {
 toast.error(t("validation.required"));
 return;
 }
 if (!contractData.recurring_duration || parseInt(contractData.recurring_duration) <= 0) {
 toast.error(t("validation.required"));
 return;
 }
 }

 // Build payment plan based on schedule type
 let paymentPlan = [];
 const downPaymentAmount = parseFloat(contractData.down_payment) || 0;
 const today = new Date().toISOString().split('T')[0];
 
 if (downPaymentAmount > 0 && contractData.down_payment_date) {
 // Down payment is usually paid at contract signing, so mark as PAID if date is today or past
 const isPaid = contractData.down_payment_date <= today;
 paymentPlan.push({
 description:"Down Payment",
 amount: downPaymentAmount,
 due_date: contractData.down_payment_date,
 type:"down_payment",
 status: isPaid ?"PAID":"PLANNED"
 });
 }

 if (contractData.payment_schedule_type ==="lump_sum") {
 // Add milestone payments
 contractData.milestone_payments.forEach((milestone, index) => {
 if (milestone.amount && milestone.due_date) {
 paymentPlan.push({
 description: milestone.description || `Payment ${index + 2}`,
 amount: parseFloat(milestone.amount),
 due_date: milestone.due_date,
 type:"milestone",
 status:"PLANNED"
 });
 }
 });
 } else {
 // Generate recurring payments
 const recurringAmount = parseFloat(contractData.recurring_amount);
 const duration = parseInt(contractData.recurring_duration);
 const frequency = contractData.recurring_frequency;
 const startDate = new Date(contractData.recurring_start_date);

 // Validate start date is valid
 if (isNaN(startDate.getTime())) {
 toast.error(t("validation.required"));
 return;
 }

 for (let i = 0; i < duration; i++) {
 const paymentDate = new Date(startDate);
 if (frequency ==="monthly") {
 paymentDate.setMonth(paymentDate.getMonth() + i);
 } else {
 paymentDate.setFullYear(paymentDate.getFullYear() + i);
 }
 
 paymentPlan.push({
 description: `${frequency ==="monthly"?"Monthly":"Yearly"} Payment ${i + 1}`,
 amount: recurringAmount,
 due_date: paymentDate.toISOString().split('T')[0],
 type:"recurring",
 status:"PLANNED"
 });
 }
 }
 
 try {
 await axios.patch(`/reservations/${selectedReservation.id}/confirm`, {
 deposit_confirmed: true,
 contract_details: {
 ...contractData,
 payment_plan: paymentPlan
 }
 });
 toast.success(t("messages.saveSuccess"));
 setConfirmSaleOpen(false);
 setContractData({
 apartment_price:"",
 payment_method:"cash",
 down_payment:"",
 down_payment_date: new Date().toISOString().split('T')[0],
 payment_schedule_type:"lump_sum",
 milestone_payments: [{ description:"", amount:"", due_date:""}],
 recurring_amount:"",
 recurring_frequency:"monthly",
 recurring_start_date:"",
 recurring_duration:"",
 contract_document_url:"",
 contract_file: null,
 contract_file_name:"",
 notes:""
 });
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to confirm sale");
 }
 };

 const handleReleaseReservation = async (e) => {
 if (e) {
 e.preventDefault();
 e.stopPropagation();
 }
 
 console.log("Release reservation clicked for:", selectedReservation?.id);
 
 const confirmed = window.confirm("Are you sure you want to release this reservation? The unit will become available again.");
 console.log("User confirmed:", confirmed);
 
 if (!confirmed) {
 return;
 }
 
 try {
 console.log("Attempting to delete reservation:", selectedReservation.id);
 const response = await axios.delete(`/reservations/${selectedReservation.id}`);
 console.log("Delete response:", response);
 toast.success(t("messages.saveSuccess"));
 setDetailOpen(false);
 await loadData();
 } catch (error) {
 console.error("Error releasing reservation:", error);
 toast.error(error.response?.data?.detail ||"Failed to release reservation");
 }
 };

 const addPlanRow = () => {
 setFormData((f) => ({
 ...f,
 payment_plan: [...(f.payment_plan || []), { amount:"", due_date:"", status:"PLANNED", paid_at: null }]
 }));
 };

 const updatePlanRow = (idx, field, value) => {
 const rows = [...formData.payment_plan];
 rows[idx] = { ...rows[idx], [field]: value };
 setFormData({ ...formData, payment_plan: rows });
 };

 const removePlanRow = (idx) => {
 const rows = [...formData.payment_plan];
 rows.splice(idx, 1);
 setFormData({ ...formData, payment_plan: rows });
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 const payload = {
 unit_id: formData.unit_id,
 buyer_name: formData.buyer_name,
 buyer_phone: formData.buyer_phone,
 buyer_email: formData.buyer_email,
 contract_value: parseFloat(formData.contract_value || 0),
 deposit_amount: parseFloat(formData.deposit_amount || 2000),
 payment_plan: (formData.payment_plan || []).map((r) => ({
 amount: parseFloat(r.amount || 0),
 due_date: r.due_date,
 status: r.status ||"PLANNED",
 paid_at: r.paid_at || null,
 })),
 };
 await axios.post("/reservations", payload);
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 setFormData({ unit_id:"", buyer_name:"", buyer_phone:"", buyer_email:"", contract_value:"", deposit_amount:"2000", deposit_paid: false, payment_plan: [] });
 setSelectedProjectId(""); // Reset project selection
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to create reservation");
 }
 };

 const statusColors = {
 hold:"bg-amber-500/10 text-amber-700 border-orange-200/60",
 confirmed:"bg-emerald-500/10 text-emerald-700 border-green-200/60",
 expired:"bg-rose-500/10 text-rose-700 border-red-200/60",
 cancelled:"bg-white/60 text-slate-800 border-slate-200"
 };

 // Check if a reservation hold has expired
 const isHoldExpired = (reservation) => {
 if (reservation.status !=="hold"|| !reservation.held_until) return false;
 return new Date(reservation.held_until) < new Date();
 };

 // Handle canceling expired reservation
 const handleCancelExpiredReservation = async (reservationId, e) => {
 e.stopPropagation();
 if (!window.confirm("Cancel this expired reservation? The unit will become available again.")) {
 return;
 }
 try {
 await axios.delete(`/reservations/${reservationId}`);
 toast.success(t("messages.deleteSuccess"));
 loadData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const handleSendPaymentReminder = async (reservationId, e) => {
 if (e) e.stopPropagation();
 try {
 const { data } = await axios.post(`/reservations/${reservationId}/send-payment-reminder`);
 toast.success(data.message);
 } catch (error) {
 toast.error(error.response?.data?.detail || "Failed to send reminder");
 }
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="reservations-page"className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t('reservations.title')}</h1>
 <p className="text-sm text-slate-500">{t('reservations.subtitle')}</p>
 </div>
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogTrigger asChild>
 <Button data-testid="create-reservation-button"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/>
 {t('reservations.createReservation')}
 </Button>
 </DialogTrigger>
 <DialogContent data-testid="create-reservation-dialog"className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>{t('reservations.newReservation')}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label>{t('common.project')}</Label>
 <Select 
 value={selectedProjectId} 
 onValueChange={(val) => {
 setSelectedProjectId(val);
 setFormData({ ...formData, unit_id:""}); // Reset unit selection when project changes
 }}
 >
 <SelectTrigger data-testid="reservation-project-select">
 <SelectValue placeholder={t('reservations.selectProjectFirst')} />
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>{t('reservations.availableUnit')}</Label>
 <Select 
 value={formData.unit_id} 
 onValueChange={(val) => setFormData({ ...formData, unit_id: val })}
 disabled={!selectedProjectId}
 >
 <SelectTrigger data-testid="reservation-unit-select">
 <SelectValue placeholder={selectedProjectId ? t('reservations.selectAvailableUnit') : t('reservations.selectProjectFirst')} />
 </SelectTrigger>
 <SelectContent>
 {getAvailableUnitsForProject().map((unit) => (
 <SelectItem key={unit.id} value={unit.id}>
 {unit.code} - {unit.bedrooms} BR, {unit.net_m2} m²
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 {selectedProjectId && getAvailableUnitsForProject().length === 0 && (
 <p className="text-sm text-orange-600 mt-1">{t('reservations.noAvailableUnits')}</p>
 )}
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <Label>{t('reservations.buyerName')}</Label>
 <Input data-testid="buyer-name-input"value={formData.buyer_name} onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })} required />
 </div>
 <div>
 <Label>{t('reservations.buyerPhone')}</Label>
 <Input data-testid="buyer-phone-input"value={formData.buyer_phone} onChange={(e) => setFormData({ ...formData, buyer_phone: e.target.value })} required />
 </div>
 </div>
 <div className="grid grid-cols-3 gap-3">
 <div>
 <Label>{t('reservations.buyerEmail')}</Label>
 <Input data-testid="buyer-email-input"type="email"value={formData.buyer_email} onChange={(e) => setFormData({ ...formData, buyer_email: e.target.value })} required />
 </div>
 <div>
 <Label>{t('reservations.contractValue')} (€)</Label>
 <Input data-testid="contract-value-input"type="number"step="0.01"value={formData.contract_value} onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })} required />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>{t('reservations.reservationFee')} (€)</Label>
 <Input data-testid="deposit-amount-input"type="number"step="0.01"value={formData.deposit_amount} onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })} required />
 </div>
 </div>

 <div className="space-y-2">
 <div className="flex items-center justify-between">
 <Label className="text-base">{t('reservations.paymentSchedule')}</Label>
 <Button type="button"variant="outline"size="sm"onClick={addPlanRow}><CalendarPlus className="w-4 h-4 mr-1"/>{t('common.addRow')}</Button>
 </div>
 {(formData.payment_plan || []).length === 0 ? (
 <div className="text-sm text-slate-500">{t('reservations.noScheduleRows')}</div>
 ) : (
 <div className="space-y-2">
 {(formData.payment_plan || []).map((row, idx) => (
 <div key={idx} className="grid grid-cols-4 gap-2 items-center">
 <Input type="number"step="0.01"placeholder={t('common.amount')} value={row.amount} onChange={(e) => updatePlanRow(idx, 'amount', e.target.value)} />
 <Input type="date"value={row.due_date} onChange={(e) => updatePlanRow(idx, 'due_date', e.target.value)} />
 <Select value={row.status} onValueChange={(v) => updatePlanRow(idx, 'status', v)}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="PLANNED">{t('common.planned')}</SelectItem>
 <SelectItem value="PAID">{t('common.paid')}</SelectItem>
 <SelectItem value="OVERDUE">{t('common.overdue')}</SelectItem>
 </SelectContent>
 </Select>
 <Button type="button"variant="outline"onClick={() => removePlanRow(idx)}>{t('common.remove')}</Button>
 </div>
 ))}
 </div>
 )}
 </div>

 <Button type="submit"data-testid="submit-reservation-button"className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 {t('reservations.createReservation')}
 </Button>
 </form>
 </DialogContent>
 </Dialog>
 </div>

 {loading ? (
 <div className="flex items-center justify-center h-64"><div className="text-sm text-slate-500">{t('common.loading')}</div></div>
 ) : (
 <div className="space-y-6">
 {Object.values(getReservationsGroupedByProject()).map((projectGroup) => {
 if (projectGroup.reservations.length === 0) return null;
 const isExpanded = expandedProjects[projectGroup.project.id];
 
 return (
 <div key={projectGroup.project.id} className="space-y-4">
 {/* Project Header - Clickable */}
 <div 
 className="flex items-center gap-3 pb-2 border-b-2 border-blue-500 cursor-pointer hover:bg-white/80 transition-colors p-2 rounded-t-lg"
 onClick={() => toggleProject(projectGroup.project.id)}
 >
 {isExpanded ? (
 <ChevronDown className="w-5 h-5 text-blue-600"/>
 ) : (
 <ChevronRight className="w-5 h-5 text-blue-600"/>
 )}
 <Building2 className="w-6 h-6 text-blue-600"/>
 <div className="flex-1">
 <h2 className="text-xl font-bold text-slate-900">{projectGroup.project.name}</h2>
 <p className="text-sm text-slate-500">{projectGroup.project.code} • {projectGroup.project.city}</p>
 </div>
 <div className="text-sm text-slate-500">
 {projectGroup.reservations.length} reservations
 </div>
 </div>

 {/* Reservations - Collapsible */}
 {isExpanded && (
 <div className="grid grid-cols-1 gap-4">
 {projectGroup.reservations.map((reservation) => {
 const expired = isHoldExpired(reservation);
 
 return (
 <Card 
 key={reservation.id} 
 data-testid={`reservation-card-${reservation.id}`} 
 className={`hover:shadow-lg transition-shadow cursor-pointer ${
 expired 
 ? 'border-red-400 bg-red-50/80 border-2' 
 : 'border-slate-200'
 }`}
 onClick={() => {
 setSelectedReservation(reservation);
 setDetailOpen(true);
 }}
 >
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-3 flex-1">
 <DollarSign className={`w-6 h-6 mt-1 ${expired ? 'text-red-600 ' : 'text-green-600 '}`} />
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 <h3 className="font-medium text-slate-900">{reservation.buyer_name}</h3>
 <Badge variant="outline"className={
 expired 
 ?"bg-rose-500/10 text-rose-700 border-red-200/60"
 : (statusColors[reservation.status] ||"bg-white/60 text-slate-800 border-slate-200")
 }>
 {expired ?"EXPIRED": reservation.status}
 </Badge>
 </div>
 <p className="text-sm text-slate-600 mb-2">{reservation.buyer_email} | {reservation.buyer_phone}</p>
 <div className="flex items-center gap-4 text-[11px] text-slate-500">
 <span>Unit: {reservation.unit_code || reservation.unit_id.substring(0, 8) + '...'}</span>
 {typeof reservation.contract_value === 'number' && (
 <span>Contract: €{reservation.contract_value.toLocaleString()}</span>
 )}
 </div>
 {expired && reservation.held_until && (
 <p className="text-xs text-red-600 mt-2 font-medium">
 ⚠️ Hold expired on {new Date(reservation.held_until).toLocaleString()}
 </p>
 )}
 </div>
 </div>
 {/* Cancel button for expired holds */}
 {expired && (
 <Button
 variant="destructive"
 size="sm"
 onClick={(e) => handleCancelExpiredReservation(reservation.id, e)}
 className="ml-2"
 data-testid={`cancel-expired-${reservation.id}`}
 >
 <X className="w-4 h-4 mr-1"/>
 Cancel
 </Button>
 )}
 </div>
 </CardContent>
 </Card>
 )})}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>

 {/* Reservation Detail Modal */}
 <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
 <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Reservation Details</DialogTitle>
 </DialogHeader>
 {selectedReservation && (
 <div className="space-y-6">
 {/* Buyer Information */}
 <div className="space-y-3">
 <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Buyer Information</h3>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <span className="text-slate-600">Name:</span>
 <span className="ml-2 font-medium text-slate-900">{selectedReservation.buyer_name}</span>
 </div>
 <div>
 <span className="text-slate-600">Phone:</span>
 <span className="ml-2 font-medium text-slate-900">{selectedReservation.buyer_phone}</span>
 </div>
 <div className="col-span-2">
 <span className="text-slate-600">Email:</span>
 <span className="ml-2 font-medium text-slate-900">{selectedReservation.buyer_email}</span>
 </div>
 </div>
 </div>

 {/* Unit & Financial Information */}
 <div className="space-y-3">
 <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Unit & Financial Details</h3>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <span className="text-slate-600">Unit:</span>
 <span className="ml-2 font-medium text-slate-900">{selectedReservation.unit_code || selectedReservation.unit_id}</span>
 </div>
 <div>
 <span className="text-slate-600">Status:</span>
 <Badge variant="outline"className={`ml-2 ${statusColors[selectedReservation.status]}`}>
 {selectedReservation.status}
 </Badge>
 </div>
 {typeof selectedReservation.contract_value === 'number' && (
 <div>
 <span className="text-slate-600">Contract Value:</span>
 <span className="ml-2 font-medium text-slate-900">€{selectedReservation.contract_value.toLocaleString()}</span>
 </div>
 )}
 {selectedReservation.deposit_amount && (
 <div>
 <span className="text-slate-600">Deposit:</span>
 <span className="ml-2 font-medium text-slate-900">€{selectedReservation.deposit_amount.toLocaleString()}</span>
 </div>
 )}
 {selectedReservation.contract_document_url && (
 <div className="col-span-2">
 <span className="text-slate-600">Contract Document:</span>
 <a 
 href={selectedReservation.contract_document_url} 
 target="_blank"
 rel="noopener noreferrer"
 className="ml-2 text-blue-600 hover:text-blue-800 underline"
 >
 View Contract
 </a>
 </div>
 )}
 {selectedReservation.status ==="hold"&& selectedReservation.held_until && (
 <div className="col-span-2">
 <span className="text-slate-600">Hold Expires:</span>
 <span className="ml-2 font-medium text-orange-600">
 {new Date(selectedReservation.held_until).toLocaleString()}
 </span>
 </div>
 )}
 </div>
 </div>

 {/* Payment Schedule */}
 {selectedReservation.payment_plan && selectedReservation.payment_plan.length > 0 && (
 <div className="space-y-3">
 <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Payment Schedule</h3>
 <div className="border border-slate-200 rounded-sm overflow-hidden">
 <table className="w-full text-sm">
 <thead className="bg-white/50">
 <tr>
 <th className="text-left p-3 text-slate-600 font-medium">Amount</th>
 <th className="text-left p-3 text-slate-600 font-medium">Due Date</th>
 <th className="text-left p-3 text-slate-600 font-medium">Status</th>
 <th className="text-left p-3 text-slate-600 font-medium">Paid At</th>
 </tr>
 </thead>
 <tbody>
 {selectedReservation.payment_plan.map((payment, idx) => (
 <tr key={idx} className="border-t border-white/40">
 <td className="p-3">€{typeof payment.amount === 'number' ? payment.amount.toLocaleString() : payment.amount}</td>
 <td className="p-3">{payment.due_date}</td>
 <td className="p-3">
 <Badge 
 variant="outline"
 className={
 payment.status ==="PAID"?"bg-emerald-500/10 text-emerald-700 border-green-200/60":
 payment.status ==="OVERDUE"?"bg-rose-500/10 text-rose-700 border-red-200/60":
"bg-white/60 text-slate-800 border-slate-200"
 }
 >
 {payment.status}
 </Badge>
 </td>
 <td className="p-3">{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() :"-"}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 )}

 {/* Payment Reminder Button */}
 {selectedReservation.payment_plan && selectedReservation.payment_plan.some(p => p.status === "PLANNED" || p.status === "OVERDUE") && selectedReservation.buyer_email && (
 <div className="pt-1">
 <Button
 variant="outline"
 size="sm"
 data-testid="send-payment-reminder-btn"
 onClick={(e) => handleSendPaymentReminder(selectedReservation.id, e)}
 className="text-amber-700 border-amber-300 hover:bg-amber-50"
 type="button"
 >
 <Bell className="w-4 h-4 mr-2"/>
 Send Payment Reminder
 </Button>
 </div>
 )}

 {/* Action Buttons */}
 <div className="flex gap-3 pt-4 border-t">
 {selectedReservation.status !=="confirmed"&& selectedReservation.status !=="sold"&& (
 <Button 
 onClick={(e) => {
 e.stopPropagation();
 openConfirmSaleForm();
 }}
 className="flex-1 bg-green-600 hover:bg-green-700"
 type="button"
 >
 <CheckCircle className="w-4 h-4 mr-2"/>
 Confirm Sale
 </Button>
 )}
 <Button 
 onClick={handleReleaseReservation}
 variant="destructive"
 className="flex-1"
 type="button"
 >
 <X className="w-4 h-4 mr-2"/>
 Delete Reservation
 </Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>

 {/* Confirm Sale Contract Form */}
 <Dialog open={confirmSaleOpen} onOpenChange={setConfirmSaleOpen}>
 <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Confirm Sale - Contract Details</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleConfirmSale} className="space-y-4">
 <div className="bg-blue-50/80 p-3 rounded-sm border border-blue-200/60">
 <p className="text-sm text-blue-800">
 <strong>Buyer:</strong> {selectedReservation?.buyer_name} | <strong>Unit:</strong> {selectedReservation?.unit_code}
 </p>
 </div>

 {/* Basic Info */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="apartment_price">Apartment Price (€) *</Label>
 <Input
 id="apartment_price"
 type="number"
 step="0.01"
 value={contractData.apartment_price}
 onChange={(e) => setContractData({ ...contractData, apartment_price: e.target.value })}
 required
 placeholder="e.g., 150000"
 />
 </div>
 <div>
 <Label htmlFor="payment_method">Payment Type *</Label>
 <Select 
 value={contractData.payment_method} 
 onValueChange={(val) => setContractData({ ...contractData, payment_method: val })}
 >
 <SelectTrigger id="payment_method">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="cash">Cash</SelectItem>
 <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
 <SelectItem value="mortgage">Mortgage</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 {/* Down Payment Section */}
 <div className="p-4 bg-white/50 rounded-sm border">
 <h3 className="font-semibold text-slate-900 mb-3">Initial Down Payment</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="down_payment">Amount (€)</Label>
 <Input
 id="down_payment"
 type="number"
 step="0.01"
 value={contractData.down_payment}
 onChange={(e) => setContractData({ ...contractData, down_payment: e.target.value })}
 placeholder="e.g., 30000"
 />
 </div>
 <div>
 <Label htmlFor="down_payment_date">Payment Date</Label>
 <Input
 id="down_payment_date"
 type="date"
 value={contractData.down_payment_date}
 onChange={(e) => setContractData({ ...contractData, down_payment_date: e.target.value })}
 />
 </div>
 </div>
 </div>

 {/* Payment Schedule Type Selection */}
 <div className="p-4 bg-white/50 rounded-sm border">
 <h3 className="font-semibold text-slate-900 mb-3">Payment Schedule</h3>
 <div className="flex gap-4 mb-4">
 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="radio"
 name="payment_schedule_type"
 value="lump_sum"
 checked={contractData.payment_schedule_type ==="lump_sum"}
 onChange={(e) => setContractData({ ...contractData, payment_schedule_type: e.target.value })}
 className="w-4 h-4 text-blue-600"
 />
 <span className="text-sm font-medium">Milestone Payments</span>
 <span className="text-[11px] text-slate-500">(Custom amounts at specific dates)</span>
 </label>
 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="radio"
 name="payment_schedule_type"
 value="recurring"
 checked={contractData.payment_schedule_type ==="recurring"}
 onChange={(e) => setContractData({ ...contractData, payment_schedule_type: e.target.value })}
 className="w-4 h-4 text-blue-600"
 />
 <span className="text-sm font-medium">Recurring Payments</span>
 <span className="text-[11px] text-slate-500">(Monthly/Yearly installments)</span>
 </label>
 </div>

 {/* Lump Sum / Milestone Payments */}
 {contractData.payment_schedule_type ==="lump_sum"&& (
 <div className="space-y-3">
 <p className="text-sm text-slate-600">Add payment milestones (e.g., 40% after 6 months, 30% at completion)</p>
 {contractData.milestone_payments.map((milestone, index) => (
 <div key={index} className="flex gap-2 items-end">
 <div className="flex-1">
 <Label className="text-xs">Description</Label>
 <Input
 placeholder="e.g., Second Payment"
 value={milestone.description}
 onChange={(e) => updateMilestonePayment(index, 'description', e.target.value)}
 />
 </div>
 <div className="w-32">
 <Label className="text-xs">Amount (€)</Label>
 <Input
 type="number"
 step="0.01"
 placeholder="50000"
 value={milestone.amount}
 onChange={(e) => updateMilestonePayment(index, 'amount', e.target.value)}
 />
 </div>
 <div className="w-40">
 <Label className="text-xs">Due Date</Label>
 <Input
 type="date"
 value={milestone.due_date}
 onChange={(e) => updateMilestonePayment(index, 'due_date', e.target.value)}
 />
 </div>
 {contractData.milestone_payments.length > 1 && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 className="text-red-600 hover:text-red-700"
 onClick={() => removeMilestonePayment(index)}
 >
 <X className="w-4 h-4"/>
 </Button>
 )}
 </div>
 ))}
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={addMilestonePayment}
 className="mt-2"
 >
 <Plus className="w-4 h-4 mr-1"/>
 Add Payment Milestone
 </Button>
 </div>
 )}

 {/* Recurring Payments */}
 {contractData.payment_schedule_type ==="recurring"&& (
 <div className="space-y-3">
 <p className="text-sm text-slate-600">Set up regular payment installments</p>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Payment Amount (€)</Label>
 <Input
 type="number"
 step="0.01"
 placeholder="e.g., 1000"
 value={contractData.recurring_amount}
 onChange={(e) => setContractData({ ...contractData, recurring_amount: e.target.value })}
 />
 </div>
 <div>
 <Label>Frequency</Label>
 <Select 
 value={contractData.recurring_frequency} 
 onValueChange={(val) => setContractData({ ...contractData, recurring_frequency: val })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="monthly">Monthly</SelectItem>
 <SelectItem value="yearly">Yearly</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Start Date</Label>
 <Input
 type="date"
 value={contractData.recurring_start_date}
 onChange={(e) => setContractData({ ...contractData, recurring_start_date: e.target.value })}
 />
 </div>
 <div>
 <Label>Number of Payments</Label>
 <Input
 type="number"
 placeholder="e.g., 24"
 value={contractData.recurring_duration}
 onChange={(e) => setContractData({ ...contractData, recurring_duration: e.target.value })}
 />
 </div>
 </div>
 </div>
 )}

 {/* Payment Summary */}
 {contractData.apartment_price && (
 <div className="mt-4 p-3 bg-white rounded border">
 <h4 className="text-sm font-semibold text-[#334155] mb-2">Payment Summary</h4>
 <div className="text-sm space-y-1">
 <div className="flex justify-between">
 <span>Apartment Price:</span>
 <span className="font-medium">€{parseFloat(contractData.apartment_price || 0).toLocaleString()}</span>
 </div>
 <div className="flex justify-between">
 <span>Down Payment:</span>
 <span className="font-medium">€{parseFloat(contractData.down_payment || 0).toLocaleString()}</span>
 </div>
 {contractData.payment_schedule_type ==="lump_sum"&& (
 <div className="flex justify-between">
 <span>Milestone Payments:</span>
 <span className="font-medium">
 €{contractData.milestone_payments.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0).toLocaleString()}
 </span>
 </div>
 )}
 {contractData.payment_schedule_type ==="recurring"&& (
 <div className="flex justify-between">
 <span>
 {contractData.recurring_duration || 0} × €{parseFloat(contractData.recurring_amount || 0).toLocaleString()} ({contractData.recurring_frequency}):
 </span>
 <span className="font-medium">
 €{((parseFloat(contractData.recurring_amount) || 0) * (parseInt(contractData.recurring_duration) || 0)).toLocaleString()}
 </span>
 </div>
 )}
 <div className="flex justify-between pt-2 border-t font-semibold">
 <span>Remaining Balance:</span>
 <span className={calculateRemainingBalance() === 0 ?"text-green-600": calculateRemainingBalance() < 0 ?"text-red-600":"text-orange-600"}>
 €{calculateRemainingBalance().toLocaleString()}
 </span>
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Contract Document Upload */}
 <div>
 <Label>Contract Document *</Label>
 <input
 type="file"
 ref={contractFileInputRef}
 onChange={handleContractFileSelect}
 accept=".pdf,.jpg,.jpeg,.png,.webp"
 className="hidden"
 />
 
 {contractData.contract_document_url ? (
 <div className="flex items-center gap-3 p-3 bg-green-50/80 border border-green-200/60 rounded-sm">
 {contractData.contract_file_name?.toLowerCase().endsWith('.pdf') ? (
 <FileText className="w-8 h-8 text-red-600"/>
 ) : (
 <Image className="w-8 h-8 text-blue-600"/>
 )}
 <div className="flex-1">
 <p className="font-medium text-slate-900">{contractData.contract_file_name ||"Contract uploaded"}</p>
 <p className="text-xs text-green-600">File uploaded successfully</p>
 </div>
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => {
 setContractData({ ...contractData, contract_document_url:"", contract_file_name:""});
 }}
 >
 Remove
 </Button>
 </div>
 ) : (
 <div 
 className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
 uploadingContract ? 'border-blue-300 bg-blue-50/80' : 'border-slate-200 hover:border-blue-400 hover:bg-white/80 '
 }`}
 onClick={() => !uploadingContract && contractFileInputRef.current?.click()}
 >
 {uploadingContract ? (
 <div className="flex flex-col items-center">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
 <p className="text-blue-600">Uploading contract...</p>
 </div>
 ) : (
 <>
 <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2"/>
 <p className="text-slate-600 font-medium">Click to upload contract document</p>
 <p className="text-[11px] text-slate-500 mt-1">PDF, JPG, PNG, WebP (max 25MB)</p>
 </>
 )}
 </div>
 )}
 </div>

 <div>
 <Label htmlFor="notes">Additional Notes</Label>
 <textarea
 id="notes"
 className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={contractData.notes}
 onChange={(e) => setContractData({ ...contractData, notes: e.target.value })}
 placeholder="Add any special terms, conditions, or notes about the sale..."
 />
 </div>

 <div className="flex gap-3 pt-4 border-t">
 <Button
 type="button"
 variant="outline"
 onClick={() => setConfirmSaleOpen(false)}
 className="flex-1"
 >
 Cancel
 </Button>
 <Button
 type="submit"
 className="flex-1 bg-green-600 hover:bg-green-700"
 disabled={!contractData.contract_document_url}
 >
 <CheckCircle className="w-4 h-4 mr-2"/>
 Confirm Sale
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default ReservationsPage;
